import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const LOGO_SRC = "/brand/pazapa-logo.jpg";

export default function Header({ onTalk }) {
  const { lang, setLang, t } = useLang();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const isStandalone =
    typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches;

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      setOpen(false);
      return;
    }
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    toast(t(isIOS ? "install.ios" : "install.generic"), { duration: 9000 });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const prefix = location.pathname === "/" ? "" : "/";
  const links = [
    { label: t("nav.home"), href: `${prefix}#top`, id: "home" },
    { label: t("nav.activities"), href: `${prefix}#activites`, id: "activities" },
    { label: t("nav.inclusion"), href: "/inclusion", id: "inclusion", route: true },
    { label: t("nav.families"), href: `${prefix}#familles`, id: "families" },
    { label: t("nav.agenda"), href: `${prefix}#agenda`, id: "agenda" },
    { label: t("nav.about"), href: `${prefix}#a-propos`, id: "about" },
    { label: t("nav.support"), href: `${prefix}#soutenir`, id: "support" },
    { label: t("nav.contact"), href: `${prefix}#contact`, id: "contact" },
  ];

  const linkClass =
    "text-sm font-semibold text-foreground/85 hover:text-primary transition-colors";

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled || location.pathname !== "/" ? "bg-background/95 backdrop-blur border-b border-border" : "bg-background/70 backdrop-blur-sm"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-20 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="header-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <img
              src={LOGO_SRC}
              alt="PAZAPA"
              className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover"
            />
            <div className="hidden sm:block">
              <div className="font-heading text-xl md:text-2xl text-primary leading-none">PAZAPA</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-foreground/60 mt-1">
                {t("nav.molenbeek")}
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6" aria-label={t("nav.home")}>
            {links.map((l) =>
              l.route ? (
                <Link key={l.id} to={l.href} data-testid={`nav-${l.id}`} className={linkClass}>
                  {l.label}
                </Link>
              ) : (
                <a key={l.id} href={l.href} data-testid={`nav-${l.id}`} className={linkClass}>
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden sm:flex items-center gap-1 text-sm font-semibold" role="group" aria-label="Langue">
              <button
                onClick={() => setLang("fr")}
                data-testid="lang-fr"
                className={`px-2 py-1 rounded transition-colors ${
                  lang === "fr" ? "text-primary" : "text-foreground/50 hover:text-foreground"
                }`}
                aria-pressed={lang === "fr"}
              >
                FR
              </button>
              <span className="text-foreground/25">|</span>
              <button
                onClick={() => setLang("nl")}
                data-testid="lang-nl"
                className={`px-2 py-1 rounded transition-colors ${
                  lang === "nl" ? "text-primary" : "text-foreground/50 hover:text-foreground"
                }`}
                aria-pressed={lang === "nl"}
              >
                NL
              </button>
            </div>

            <button
              onClick={onTalk}
              data-testid="header-talk-btn"
              className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 hidden md:inline-flex"
            >
              <Phone className="w-4 h-4" strokeWidth={2.5} />
              {t("nav.talk")}
            </button>

            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-btn"
              className="xl:hidden p-2.5 rounded-full border border-border bg-white hover:bg-muted"
              aria-label={t("nav.open_menu")}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-background"
            data-testid="mobile-menu"
          >
            <div className="h-20 px-4 md:px-8 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-3">
                <img src={LOGO_SRC} alt="" className="w-11 h-11 rounded-full object-cover" />
                <div className="font-heading text-xl text-primary">PAZAPA</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label={t("nav.close_menu")}
                className="p-2.5 rounded-full border border-border"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="px-6 pt-10 flex flex-col gap-5">
              {links.map((l, i) =>
                l.route ? (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                  >
                    <Link
                      to={l.href}
                      onClick={() => setOpen(false)}
                      className="font-heading text-3xl text-foreground hover:text-primary"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={l.id}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                    className="font-heading text-3xl text-foreground hover:text-primary"
                  >
                    {l.label}
                  </motion.a>
                )
              )}
              <div className="mt-6 flex items-center gap-4 text-lg font-semibold">
                <button
                  onClick={() => setLang("fr")}
                  data-testid="mobile-lang-fr"
                  className={`px-3 py-1.5 rounded-full ${lang === "fr" ? "bg-primary text-white" : "bg-muted"}`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("nl")}
                  data-testid="mobile-lang-nl"
                  className={`px-3 py-1.5 rounded-full ${lang === "nl" ? "bg-primary text-white" : "bg-muted"}`}
                >
                  NL
                </button>
              </div>
              <button
                onClick={() => {
                  setOpen(false);
                  onTalk?.();
                }}
                className="pill-btn bg-primary text-primary-foreground mt-6 justify-center"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                {t("nav.talk")}
              </button>
              {!isStandalone && (
                <button
                  onClick={handleInstall}
                  data-testid="mobile-install-btn"
                  className="pill-btn border-2 border-primary/40 text-primary mt-2 justify-center"
                >
                  <Smartphone className="w-4 h-4" strokeWidth={2.5} />
                  {t("nav.install")}
                </button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
